import { useCallback, useEffect, useState } from "react";
import { FileDown, HandCoins, History, Search } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useMaster } from "@/lib/useMaster";
import { formatDate, formatDateTime, formatIDR } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Receivables() {
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const { productMap } = useMaster();

  const load = useCallback(() => {
    api.get("/ar").then((r) => setData(r.data)).catch(() => {});
    api.get("/ar/payments").then((r) => setPayments(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const openSettle = (c) => {
    setSelected(c);
    setChecked(Object.fromEntries(c.invoices.map((i) => [i.id, true])));
  };

  const selectedTotal = selected
    ? selected.invoices.filter((i) => checked[i.id]).reduce((s, i) => s + i.subtotal, 0)
    : 0;

  const pay = async () => {
    const ids = selected.invoices.filter((i) => checked[i.id]).map((i) => i.id);
    if (ids.length === 0) {
      toast.error("Select at least one invoice");
      return;
    }
    setBusy(true);
    try {
      const { data: p } = await api.post("/ar/payments", {
        customer_name: selected.customer_name,
        invoice_ids: ids,
      });
      toast.success(`Payment ${p.payment_no} received — ${formatIDR(p.total)}`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const customers = (data?.customers || []).filter(
    (c) =>
      !search ||
      c.customer_name.toLowerCase().includes(search.toLowerCase()) ||
      (c.customer_phone || "").includes(search)
  );

  return (
    <div className="space-y-8" data-testid="receivables-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Receivables (AR)</h1>
          <p className="mt-1 text-sm text-zinc-500">Track unpaid invoices per customer and settle them in one payment.</p>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white px-6 py-4">
          <p className="text-xs uppercase tracking-wide text-zinc-400">Total Outstanding</p>
          <p className="tnum mt-1 text-2xl font-bold text-amber-600" data-testid="ar-total-outstanding">
            {formatIDR(data?.total_outstanding || 0)}
          </p>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
        <Input
          data-testid="ar-customer-search"
          placeholder="Filter by customer name or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-sm border-zinc-300 bg-white pl-9 focus-visible:ring-blue-600"
        />
      </div>

      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="ar-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 text-right font-medium">Open Invoices</th>
              <th className="px-4 py-3 font-medium">Oldest Invoice</th>
              <th className="px-4 py-3 text-right font-medium">Outstanding</th>
              <th className="px-4 py-3 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-zinc-400" data-testid="ar-empty">
                  No outstanding receivables
                </td>
              </tr>
            )}
            {customers.map((c, i) => (
              <tr key={c.customer_name} onClick={() => openSettle(c)} className="cursor-pointer border-b border-zinc-100 last:border-0 transition-colors hover:bg-zinc-50" data-testid={`ar-customer-row-${i}`}>
                <td className="px-4 py-2.5 font-medium text-zinc-900">{c.customer_name}</td>
                <td className="px-4 py-2.5 text-zinc-500">{c.customer_phone || "-"}</td>
                <td className="tnum px-4 py-2.5 text-right text-zinc-700">{c.invoice_count}</td>
                <td className="px-4 py-2.5 text-zinc-500">{formatDate(c.oldest_date)}</td>
                <td className="tnum px-4 py-2.5 text-right font-semibold text-amber-600">{formatIDR(c.total_outstanding)}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`ar-print-statement-${i}`}
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_BACKEND_URL}/api/ar/statement?customer=${encodeURIComponent(c.customer_name)}`,
                        "_blank"
                      )
                    }
                    className="mr-2 rounded-sm border-zinc-300"
                  >
                    <FileDown className="mr-2 h-4 w-4" /> Statement
                  </Button>
                  <Button
                    size="sm"
                    data-testid={`ar-settle-btn-${i}`}
                    onClick={() => openSettle(c)}
                    className="rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700"
                  >
                    <HandCoins className="mr-2 h-4 w-4" /> Receive Payment
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="mb-3 flex items-center gap-2">
          <History className="h-4 w-4 text-zinc-400" />
          <h2 className="text-lg font-bold tracking-tight text-zinc-900">Payment History</h2>
        </div>
        <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
          <table className="w-full text-sm" data-testid="ar-payments-table">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                <th className="px-4 py-3 font-medium">Payment No.</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Invoices Settled</th>
                <th className="px-4 py-3 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-zinc-400" data-testid="ar-payments-empty">
                    No payments received yet
                  </td>
                </tr>
              )}
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`ar-payment-row-${p.payment_no}`}>
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-600">{p.payment_no}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{formatDateTime(p.created_at)}</td>
                  <td className="px-4 py-2.5 font-medium text-zinc-900">{p.customer_name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{p.invoice_nos.join(", ")}</td>
                  <td className="tnum px-4 py-2.5 text-right font-semibold text-emerald-600">{formatIDR(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Receive Payment — {selected?.customer_name}</DialogTitle>
            <DialogDescription className="sr-only">Select invoices to settle in one payment</DialogDescription>
          </DialogHeader>
          <div className="space-y-3" data-testid="ar-settle-dialog">
            <p className="text-sm text-zinc-500">Preview the units sold per invoice below. All unpaid invoices are selected by default — uncheck any you want to exclude.</p>
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {selected?.invoices.map((inv, i) => (
                <label
                  key={inv.id}
                  className="block cursor-pointer rounded-sm border border-zinc-200 px-3 py-2.5 transition-colors hover:bg-zinc-50"
                  data-testid={`ar-invoice-row-${i}`}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        data-testid={`ar-invoice-checkbox-${i}`}
                        checked={!!checked[inv.id]}
                        onChange={(e) => setChecked({ ...checked, [inv.id]: e.target.checked })}
                        className="h-4 w-4 accent-blue-600"
                      />
                      <span>
                        <span className="block font-mono text-xs font-semibold text-blue-600">{inv.inv_no}</span>
                        <span className="block text-xs text-zinc-500">{formatDateTime(inv.created_at)}</span>
                      </span>
                    </span>
                    <span className="flex items-center gap-3">
                      <StatusBadge status={inv.status} />
                      <span className="tnum text-sm font-semibold text-zinc-900">{formatIDR(inv.subtotal)}</span>
                    </span>
                  </span>
                  <span className="mt-2 block border-t border-dashed border-zinc-200 pt-2" data-testid={`ar-invoice-items-${i}`}>
                    {inv.items.map((it, j) => (
                      <span key={j} className="flex justify-between text-xs text-zinc-600">
                        <span>{productMap[it.product_id]?.name || "?"} ×{it.qty}</span>
                        <span className="tnum">@ {formatIDR(it.sell_price)}</span>
                      </span>
                    ))}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-sm bg-zinc-50 px-4 py-3">
              <span className="text-sm text-zinc-500">Payment Total</span>
              <span className="tnum text-xl font-bold text-zinc-900" data-testid="ar-total-selected">{formatIDR(selectedTotal)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="ar-confirm-payment" onClick={pay} disabled={busy} className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700">
              {busy ? "Processing..." : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
